/**
 * Settings > Import / Export helpers (tracker item 52): labels, which exports
 * take a date range, the preview columns, and saving a Blob as a file.
 */
import * as types from '../../api/types';

export interface ExportOption {
  dataset: types.ExportDataset;
  label: string;
  description: string;
  /** What the from / to range filters on; undefined = the whole dataset */
  dateField?: string;
}

export const EXPORT_OPTIONS: ExportOption[] = [
  { dataset: 'customers', label: 'Customers', description: 'Every customer, in the import column format.' },
  { dataset: 'properties', label: 'Properties', description: 'Every service address with its owner, billing customer and tax rate.' },
  { dataset: 'items', label: 'Items', description: 'The price list, with GL account numbers and stock on hand.' },
  { dataset: 'jobs', label: 'Jobs', description: 'Jobs with customer, address, assignee and totals.', dateField: 'scheduled date' },
  { dataset: 'invoices', label: 'Invoices', description: 'Invoices with totals, amount paid and balance.', dateField: 'invoice date' },
  { dataset: 'payments', label: 'Payments', description: 'Payments with the invoices they were applied to.', dateField: 'payment date' },
  {
    dataset: 'quickbooks-invoices',
    label: 'QuickBooks Online: invoices',
    description: 'Posted invoices in the QuickBooks Online "Import invoices" format, one row per line.',
    dateField: 'invoice date',
  },
  {
    dataset: 'quickbooks-customers',
    label: 'QuickBooks Online: customers',
    description: 'Customers with QuickBooks Online customer import column names.',
  },
];

export const ACTION_LABELS: Record<types.ImportRowAction, string> = {
  CREATE: 'New',
  SKIP: 'Already exists',
  ERROR: 'Error',
};

export const ACTION_CLASSES: Record<types.ImportRowAction, string> = {
  CREATE: 'bg-green-100 text-green-800',
  SKIP: 'bg-gray-100 text-gray-700',
  ERROR: 'bg-red-100 text-red-800',
};

/** The preview table's value columns for each entity (keys of ImportRowResult.values). */
export const PREVIEW_COLUMNS: Record<types.ImportEntity, { key: string; label: string }[]> = {
  customers: [
    { key: 'externalRef', label: 'Reference' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'status', label: 'Status' },
  ],
  properties: [
    { key: 'customer', label: 'Customer' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'zip', label: 'ZIP' },
    { key: 'taxRate', label: 'Tax rate' },
    { key: 'billingCustomer', label: 'Billed to' },
  ],
  items: [
    { key: 'name', label: 'Name' },
    { key: 'itemType', label: 'Type' },
    { key: 'unitPrice', label: 'Price' },
    { key: 'revenueAccount', label: 'Revenue acct' },
    { key: 'category', label: 'Category' },
  ],
};

/** "1 row" / "3 rows" */
export function rowCount(n: number, noun = 'row'): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** Whether the export needs nothing more than a valid (or empty) range. Returns an error message or null. */
export function exportRangeError(from: string, to: string): string | null {
  if (from && to && from > to) return 'The start date must be on or before the end date.';
  return null;
}

/** Save a Blob as a download (object URL + temporary link). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Let the browser start the download before the URL goes away
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch { /* already gone */ }
    }, 1000);
  }
}
